import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { useAuth } from '@/hooks/useAuth';
import { useChatUnreadCountQuery } from '@/hooks/useChat';
import { CHAT_UNREAD_BADGE_MAXIMUM } from '@/constants/chat';
import { cn } from '@/lib/utils';
import {
  CLIENT_NAVIGATION_ITEMS,
  filterClientNavigationItemsByEnabledModules,
  filterClientNavigationItemsForBottomBar,
} from './navigationItems';

function buildUnreadBadgeLabel(unreadCount: number): string {
  return unreadCount > CHAT_UNREAD_BADGE_MAXIMUM
    ? String(CHAT_UNREAD_BADGE_MAXIMUM) + '+'
    : String(unreadCount);
}

export function ClientBottomNavigation() {
  const { t } = useTranslation(['clients', 'chat']);
  const { enabledModules } = useAuth();
  const chatUnreadCountQuery = useChatUnreadCountQuery('client');
  const chatUnreadCount = chatUnreadCountQuery.data === undefined ? 0 : chatUnreadCountQuery.data;

  const items = filterClientNavigationItemsForBottomBar(
    filterClientNavigationItemsByEnabledModules(CLIENT_NAVIGATION_ITEMS, enabledModules)
  );

  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-zoowhite border-t border-zooborder flex"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {items.map(function renderItem(item) {
        return (
          <NavLink
            key={item.id}
            to={item.route}
            end={item.route === APPLICATION_ROUTES.clientPortal}
            className={function resolveClassName({ isActive }) {
              return cn(
                'flex-1 min-h-[44px] py-2 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors',
                isActive === true ? 'text-zooprimary' : 'text-zoomuted'
              );
            }}
          >
            <span className="relative">
              <Icon name={item.iconName} className="text-lg" />
              {item.unreadBadge === true && chatUnreadCount > 0 ? (
                <span
                  aria-label={t('chat:nav.unreadBadgeAriaLabel', { count: chatUnreadCount })}
                  className="absolute -top-1 -right-2 min-w-[14px] h-[14px] px-0.5 flex items-center justify-center rounded-full bg-zooprimary text-zoowhite text-[8px] font-bold"
                >
                  {buildUnreadBadgeLabel(chatUnreadCount)}
                </span>
              ) : null}
            </span>
            <span className="w-full truncate text-center">{t('clients:' + item.labelKey)}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}
